// MatchGroupSubsection.tsx
import React, { FC, useState } from 'react';
import { Form } from 'react-bootstrap';
import { IMatch } from '../PlayTag/PlayTag';
import { Match } from '../Match/Match';
import { Court, MatchResult, Team } from '../../../../../types/tournament';
import { TournamentMatchStatusEnum } from '../../../../../types/enums';
import { FormikProps } from 'formik';
import { PlayValues } from '../PlayTag/PlayTag';
import { CustomButton } from '../../../../../components';
import { AddIcon } from '../../../../../assets/icons/AddIcon';
import { t } from 'i18next';
import { GroupStandings } from '../GroupStandings/GroupStandings';
import { CategoryStanding } from '../../../../../apis/tournament';
import { ClassValues } from '../../../TournamentClasses/components/ClassesTag/ClassesTag';

interface MatchGroupSubsectionProps {
  title: string;
  categoryId: string;
  matchesToDisplay: IMatch[];
  isCollapsible?: boolean;
  defaultOpen?: boolean;
  isEditable?: boolean;
  allMatches: { [key: string]: IMatch };
  currentClass: ClassValues;
  courts: Court[];
  formikArg: FormikProps<PlayValues>;
  sortVs?: boolean;
  roundNumber?: number;
  groupId?: number | null;
  categoryStandings?: CategoryStanding[];
  handleAddTeam: (matchId: string | number, fieldName: string) => void;
  handleAddPlayer: (teamId: string | number, fieldName: string, matchId: string | number) => void;
  handleOpenResultModal: (results?: Array<MatchResult>, matchId?: number | string, homeTeam?: Team | null, awayTeam?: Team | null) => void;
  onAddMatch: () => void;
  onTitleChange?: (categoryId: string, newName: string) => void;
  isTeamsClass?: boolean;
  rankingUsesTeamPoints?: boolean;
}

export const MatchGroupSubsection: FC<MatchGroupSubsectionProps> = React.memo((props) => {
  const {
    title,
    categoryId,
    matchesToDisplay,
    isCollapsible = true,
    defaultOpen = true,
    isEditable = false,
    allMatches,
    currentClass,
    courts,
    formikArg,
    sortVs = false,
    roundNumber = 0,
    groupId = null,
    categoryStandings,
    handleAddTeam,
    handleAddPlayer,
    handleOpenResultModal,
    onAddMatch,
    onTitleChange,
    isTeamsClass = false,
    rankingUsesTeamPoints = false,
  } = props;

  const [isOpen, setIsOpen] = useState(defaultOpen);



  // Don't render section if no matches, unless box league
  if (matchesToDisplay.length === 0 && !currentClass.moveBox) {
    return null;
  }

  const renderMatches = () => {
    return matchesToDisplay.map((match, index) => {
      const superseded =
        match.bracketCellMatchSuperseded === true ||
        (match.bracketCellStatus === 2 &&
          match.status === TournamentMatchStatusEnum.NOT_STARTED);
      return (
        <div key={match.id} className="container-fluid px-0 my-2 m-3">
          <Match
            index={index}
            matches={allMatches}
            match={match}
            isGroup={true}
            currentClass={currentClass}
            courts={courts}
            formikArg={formikArg}
            sortVs={sortVs}
            superseded={superseded}
            handleAddTeam={handleAddTeam}
            handleAddPlayer={handleAddPlayer}
            handleOpenResultModal={handleOpenResultModal}
          />
        </div>
      );
    });
  };

  if (!isCollapsible) {
    return <div className="mb-3">{renderMatches()}</div>;
  }

  return (
    <div className="mb-3">
      <div
        onClick={() => setIsOpen(!isOpen)}
        style={{ cursor: 'pointer' }}
        className="d-flex align-items-center justify-content-between p-1 rounded"
        role="button"
        aria-expanded={isOpen}
      >
        {isEditable && onTitleChange ? (
          <div onClick={(e) => e.stopPropagation()} className="w-50">
            <Form.Control
              type="text"
              value={title}
              size="sm"
              onChange={(e) => onTitleChange(categoryId, e.target.value)}
              placeholder={t('placeholder-categoryName')}
            />
          </div>
        ) : (
          <h5 className="text-muted ps-1 mb-0">{title}</h5>
        )}

        <div className="d-flex align-items-center justify-content-between">
          {isOpen && (
            <div className="mt-2" onClick={(e) => e.stopPropagation()}>
              <CustomButton
                variant="outline-dark"
                size="sm"
                btnText={t('button-addMatch')}
                cb={onAddMatch}
                className="d-flex justify-content-between align-items-center"
              >
                <AddIcon />
              </CustomButton>
            </div>
          )}
          <span
            className="ms-2"
            style={{
              transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)',
              transition: 'transform 0.2s ease-in-out',
            }}
          >
            ▼
          </span>
        </div>
      </div>
      
      
      {isOpen && <div className="mt-2">
        {roundNumber > 0 && groupId != null && (
          <GroupStandings
            classId={+currentClass.classId}
            groupId={groupId}
            roundNumber={roundNumber}
            categoryStandings={categoryStandings}
            isTeamsClass={isTeamsClass}
            rankingUsesTeamPoints={rankingUsesTeamPoints}
          />
        )}
        {renderMatches()}
        </div>}
    </div>
  );
});
